import React, { FC, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import CloseIcon from '@mui/icons-material/Close';
import {
  Drawer,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DrawerContent } from 'shared/ui/Drawer/DrawerStyles';
import { Controller, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { createNewCompetitionThunk, updateCompetitionThunk } from 'entities/competitions-b2cr/thunk';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetKPIListQuery } from 'features/getKpiList/api/getKpiListApi';
import { useGetBrandPlansListQuery } from 'features/getBrandPlans';
import { ICompetitionsCreateFeatureForm } from '../../../../features/competitions-b2cr/types';
import { ImageUpload } from '../../../../shared/ui/ImageUpload/ui/ImageUpload';
import { PrizeListFeature } from '../../../../features/prize/list';
import { useGetAdminCompetitionItemQuery } from '../../../../entities/competitions-b2cr/api';
import { validationSchema } from '../../../../features/competitions-b2cr/lib';
import { ErrorText } from '../../../../shared/ui/error-text';
import css from '../ParticipantsXlsxLoader/style.module.css';

type IDrawerCompetition = {
  competitionId?: number;
};

const DrawerCompetition: FC<IDrawerCompetition> = ({ competitionId }) => {
  const dispatch = useAppDispatch();
  const { isDrawerOpen, actionDrawer } = useAppSelector((state) => state.ui);
  const { data, refetch: refetchCompetition } = useGetAdminCompetitionItemQuery({ competition_id: competitionId });
  const { data: kpiList } = useGetKPIListQuery({});
  const [loading, setLoading] = useState(false);
  const [isNeedCheckImage, setIsNeedCheckImage] = useState(false);
  const isInitialLoadRef = useRef(true);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    watch,
    reset,
    clearErrors,
    formState: { errors: formErrors },
  } = useForm<ICompetitionsCreateFeatureForm>({
    defaultValues: {
      description: '',
      is_zero_prized: true,
      description_short: '',
      name: '',
      date_start: '',
      date_end: '',
      date_award_start: '',
      date_award_end: '',
      kpi_name_ids: [],
      brand_ids: [],
    },
    resolver: yupResolver(validationSchema) as any,
  });

  // Watch KPI selection to reactively fetch brand plans
  const watchedKpiIds = watch('kpi_name_ids');
  console.log('👀 Watched KPI IDs changed:', watchedKpiIds);

  const { data: brandPlansList, isLoading: brandsLoading } = useGetBrandPlansListQuery(
    { kpi_ids: watchedKpiIds || [] },
    { skip: !watchedKpiIds?.length }
  );

  console.log('📦 Brand plans list data:', brandPlansList);

  const handleOnSubmit = handleSubmit(async () => {
    const errors = Object.keys(formErrors).length > 0;
    if (errors) {
      return;
    }
    setLoading(true);
    const data = getValues();

    if (!competitionId) {
      await dispatch(createNewCompetitionThunk(data)).then(handleCreatingCompetitionThunk);
    } else {
      await dispatch(updateCompetitionThunk({ ...data, id: competitionId })).then((res) => {
        handleCreatingCompetitionThunk(res);
      });
    }

    setLoading(false);
  });

  const handleCreatingCompetitionThunk = (res: any) => {
    const { response, status, detail } = res.payload;
    if (status === 400 || status === 422) {
      if (detail && detail.length > 0) {
        detail.map(({ msg, loc }: any) => {
          if (loc[1]) {
            setError(`${loc[1]}` as any, { message: msg });
          } else if (!loc.length) {
            toast.error(msg);
          }
        });
      }
      toast.error('Исправьте ошибки');
    } else if (response) {
      reset();
      refetchCompetition();
    }
  };

  const uploadImage = async (file: File) => {
    const formData = new FormData();
    formData.append('image_main', file);

    setValue('image_main', file);
    setIsNeedCheckImage(true);
  };

  /**
   * Получить время с разницой в n-дней
   */
  const getDateDiff = (value: string, days: number) => {
    const date = new Date(value);
    date.setDate(date.getDate() + days);

    return dayjs(date);
  };

  useEffect(() => {
    if (data) {
      console.log('🔄 Form initialization - data from API:', {
        kpi_name_ids: data.kpi_name_ids,
        brand_ids: data.brand_ids,
      });
      isInitialLoadRef.current = true;
      reset({
        description: data.description,
        description_short: data.description_short,
        name: data.name,
        date_start: data.date_start,
        date_end: data.date_end,
        date_award_start: data.date_award_start,
        date_award_end: data.date_award_end,
        kpi_name_ids: data.kpi_name_ids,
        brand_ids: data.brand_ids,
        image_main: data.image_main as any,
      });
      console.log('✅ Form reset complete');
      // Set flag to false after next render cycle
      setTimeout(() => {
        isInitialLoadRef.current = false;
        console.log('✅ Initial load complete, clearing flag');
      }, 0);
    }
  }, [data, reset]);

  // Reset brand selection when KPI selection changes (but not during initial form load)
  useEffect(() => {
    console.log('🔍 KPI watch effect triggered:', {
      isInitialLoad: isInitialLoadRef.current,
      watchedKpiIds,
      currentBrandIds: getValues('brand_ids'),
    });

    if (!isInitialLoadRef.current && watchedKpiIds && watchedKpiIds.length > 0) {
      const currentBrandIds = getValues('brand_ids') || [];
      console.log('⚠️ Inside clearing condition - currentBrandIds:', currentBrandIds);
      if (currentBrandIds.length > 0) {
        console.log('❌ Clearing brand_ids');
        setValue('brand_ids', []);
      }
    }
  }, [watchedKpiIds, setValue, getValues]);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => dispatch(setDrawerOpen(false))}
    >
      <DrawerContent style={{ width: '800px' }}>
        <div className="header">
          <Typography
            variant="h5"
            sx={{ fontWeight: '600' }}
          >
            {actionDrawer === 'create' ? 'Создание соревнования' : 'Изменение соревнования'}
          </Typography>
          <IconButton
            onClick={() => dispatch(setDrawerOpen(false))}
            sx={{ color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="content">
          <div>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '24px',
                  }}
                >
                  <TextField
                    type="text"
                    label="Название"
                    placeholder=""
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={formErrors.name && true}
                    {...field}
                  />
                  {formErrors.name ? <ErrorText>{formErrors.name.message}</ErrorText> : null}
                </Box>
              )}
            />

            <Controller
              name="description_short"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '24px',
                  }}
                >
                  <TextField
                    type="text"
                    label="Краткое описание"
                    placeholder=""
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={formErrors.description_short && true}
                    {...field}
                  />
                  {formErrors.description_short ? <ErrorText>{formErrors.description_short.message}</ErrorText> : null}
                </Box>
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Box
                  sx={{
                    marginBottom: '12px',
                  }}
                >
                  <TextField
                    multiline
                    type="text"
                    label="Описание"
                    placeholder=""
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={formErrors.description && true}
                    {...field}
                  />
                  {formErrors.description ? <ErrorText>{formErrors.description.message}</ErrorText> : null}
                </Box>
              )}
            />
            <Controller
              name="is_zero_prized"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
                  <Typography variant="body1">Выдавать призы при 0 баллов</Typography>
                  <Checkbox
                    sx={{ marginLeft: '8px' }}
                    {...field}
                    checked={!!field.value}
                  />
                </Box>
              )}
            />

            <Typography
              mt={1}
              mb={1}
            >
              Период проведения
            </Typography>
            <Box sx={{ display: 'flex', marginBottom: '32px' }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  control={control}
                  name="date_start"
                  defaultValue=""
                  render={({ field: { value, onChange, ...restField } }) => (
                    <div>
                      <DatePicker
                        label="Дата начала"
                        value={value ? dayjs(value) : undefined}
                        sx={{
                          marginRight: 2,
                        }}
                        minDate={!competitionId ? dayjs(new Date()) : dayjs(value)}
                        onChange={(value) => onChange(value ? dayjs(value).format('YYYY-MM-DD') : null)}
                        {...restField}
                      />
                      {formErrors.date_start ? <ErrorText>{formErrors.date_start.message}</ErrorText> : null}
                    </div>
                  )}
                />
              </LocalizationProvider>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  control={control}
                  name="date_end"
                  defaultValue=""
                  render={({ field: { value, onChange, ...restField } }) => (
                    <div>
                      <DatePicker
                        label="Дата окончания"
                        value={value ? dayjs(value) : null}
                        minDate={getDateDiff(getValues('date_start'), 1)}
                        disablePast={!competitionId}
                        onError={(err) => {
                          if (err) {
                            setError('date_end', {
                              type: 'custom',
                              message: 'Неверная дата',
                            });
                          } else {
                            clearErrors('date_end');
                          }
                        }}
                        onChange={(value) => onChange(value ? dayjs(value).format('YYYY-MM-DD') : null)}
                        {...restField}
                      />
                      {formErrors.date_end ? <ErrorText>{formErrors.date_end.message}</ErrorText> : null}
                    </div>
                  )}
                />
              </LocalizationProvider>
            </Box>

            <Typography
              mt={1}
              mb={1}
            >
              Период вывода бонусов
            </Typography>
            <Box sx={{ display: 'flex', marginBottom: '32px' }}>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  control={control}
                  name="date_award_start"
                  defaultValue=""
                  render={({ field: { value, onChange, ...restField } }) => (
                    <div>
                      <DatePicker
                        label="Дата начала"
                        value={value ? dayjs(value) : null}
                        sx={{ marginRight: 2 }}
                        disablePast={!competitionId}
                        minDate={getDateDiff(getValues('date_end'), 1)}
                        onError={(err) => {
                          if (err) {
                            setError('date_award_start', {
                              type: 'custom',
                              message: 'Неверная дата',
                            });
                          } else {
                            clearErrors('date_award_start');
                          }
                        }}
                        onChange={(value) => onChange(value ? dayjs(value).format('YYYY-MM-DD') : null)}
                        {...restField}
                      />
                      {formErrors.date_award_start ? (
                        <ErrorText>{formErrors.date_award_start.message}</ErrorText>
                      ) : null}
                    </div>
                  )}
                />
              </LocalizationProvider>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="en-gb"
              >
                <Controller
                  control={control}
                  name="date_award_end"
                  defaultValue=""
                  render={({ field: { value, onChange, ...restField } }) => (
                    <div>
                      <DatePicker
                        label="Дата окончания"
                        value={value ? dayjs(value) : null}
                        minDate={getDateDiff(getValues('date_award_start'), 1)}
                        disablePast={!competitionId}
                        onError={(err) => {
                          if (err) {
                            setError('date_award_end', {
                              type: 'custom',
                              message: 'Неверная дата',
                            });
                          } else {
                            clearErrors('date_award_end');
                          }
                        }}
                        onChange={(value) => onChange(value ? dayjs(value).format('YYYY-MM-DD') : null)}
                        {...restField}
                      />
                      {formErrors.date_award_end ? <ErrorText>{formErrors.date_award_end.message}</ErrorText> : null}
                    </div>
                  )}
                />
              </LocalizationProvider>
            </Box>

            <Controller
              control={control}
              name="kpi_name_ids"
              render={({ field }) => (
                <FormControl
                  fullWidth
                  sx={{ marginBottom: '24px' }}
                >
                  <InputLabel id="select-label-type">KPI</InputLabel>
                  <Select
                    disabled={!kpiList}
                    labelId="select-label-type"
                    label="KPI"
                    multiple
                    error={!!formErrors.kpi_name_ids}
                    {...field}
                  >
                    {kpiList
                      ?.filter((item) => item.id !== 1 && item.id !== 2)
                      .map((item) => (
                        <MenuItem value={item.id}>
                          <Checkbox checked={getValues?.('kpi_name_ids').includes(item.id)} />
                          {item.name}
                        </MenuItem>
                      ))}
                  </Select>
                  {!kpiList ? (
                    <div
                      className={css.loader}
                      style={{ top: 28 }}
                    >
                      <CircularProgress size={20} />
                    </div>
                  ) : null}
                  {formErrors.kpi_name_ids ? <ErrorText>{formErrors.kpi_name_ids.message}</ErrorText> : null}
                </FormControl>
              )}
            />

            <Controller
              control={control}
              name="brand_ids"
              render={({ field }) => {
                console.log('🎨 Brand select render - field.value:', field.value);
                console.log('🎨 Brand select render - form values:', getValues('brand_ids'));
                return (
                  <FormControl
                    fullWidth
                    sx={{ marginBottom: '24px' }}
                  >
                    <InputLabel id="select-label-brands">Бренды</InputLabel>
                    <Select
                      disabled={!brandPlansList || !watchedKpiIds?.length || brandsLoading}
                      labelId="select-label-brands"
                      label="Бренды"
                      multiple
                      error={!!formErrors.brand_ids}
                      {...field}
                    >
                      {brandPlansList?.map((item) => (
                        <MenuItem value={item.id}>
                          <Checkbox checked={getValues?.('brand_ids').includes(item.id)} />
                          {item.name}
                        </MenuItem>
                      ))}
                    </Select>
                  {!watchedKpiIds?.length ? (
                    <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 14 }}>
                      Сначала выберите KPI для отображения доступных брендов
                    </Box>
                  ) : brandsLoading ? (
                    <div
                      className={css.loader}
                      style={{ top: 28 }}
                    >
                      <CircularProgress size={20} />
                    </div>
                  ) : !brandPlansList?.length ? (
                    <Box sx={{ mt: 1, color: 'text.secondary', fontSize: 14 }}>
                      Нет доступных брендов для выбранных KPI
                    </Box>
                  ) : null}
                  {formErrors.brand_ids ? <ErrorText>{formErrors.brand_ids.message}</ErrorText> : null}
                </FormControl>
              )}}
            />

            <Controller
              control={control}
              name="image_main"
              render={({ field }) => (
                <div>
                  <ImageUpload
                    title="Фото"
                    src={field.value}
                    callback={(val) => uploadImage(val)}
                    isLoading={false}
                    isSuccess={false}
                    silent
                  />
                  {!watch().image_main && isNeedCheckImage ? (
                    <ErrorText>Изображение является обязательным</ErrorText>
                  ) : null}
                </div>
              )}
            />
          </div>
          <Box
            sx={{
              display: 'flex',
              marginTop: '30px',
            }}
          >
            <LoadingButton
              variant="contained"
              onClick={() => {
                setIsNeedCheckImage(true);
                handleOnSubmit();
              }}
              sx={{ textTransform: 'none' }}
              loading={loading}
            >
              {data?.id ? 'Сохранить' : 'Создать'}
            </LoadingButton>
          </Box>
          <Box sx={{ marginTop: '30px' }}>
            {data?.id ? (
              <PrizeListFeature
                competitionId={data.id}
                refetchCompetition={refetchCompetition}
              />
            ) : null}
          </Box>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerCompetition;
